import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getOrders } from "../lib/api";
import AppLayout from "../components/AppLayout";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Loader2, History, ArrowRight, Clock, CheckCircle, PackageCheck } from "lucide-react";

export default function OrderHistoryPage() {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const response = await getOrders();
      setOrders(response.data);
    } catch (error) {
      toast.error("Failed to load orders");
    } finally {
      setIsLoading(false);
    }
  };

  const filteredOrders = orders.filter((order) => {
    if (filter === "all") return true;
    return order.status === filter;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getUniqueVendorCount = (items) => {
    const vendors = new Set(items.map((item) => item.vendor_name));
    return vendors.size;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending_review":
        return (
          <Badge variant="secondary" className="bg-amber-100 text-amber-700 hover:bg-amber-100">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case "finalized":
        return (
          <Badge variant="secondary" className="bg-blue-100 text-blue-700 hover:bg-blue-100">
            <CheckCircle className="w-3 h-3 mr-1" />
            Finalized
          </Badge>
        );
      case "partially_received":
        return (
          <Badge variant="secondary" className="bg-orange-100 text-orange-700 hover:bg-orange-100">
            <Clock className="w-3 h-3 mr-1" />
            Partial
          </Badge>
        );
      case "received":
        return (
          <Badge variant="secondary" className="bg-kale-100 text-kale-700 hover:bg-kale-100">
            <PackageCheck className="w-3 h-3 mr-1" />
            Received
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <AppLayout title="Order History" showBack>
      <div className="p-4 md:p-6 max-w-2xl mx-auto">
        {/* Filter Tabs */}
        <Tabs value={filter} onValueChange={setFilter} className="mb-4">
          <TabsList className="w-full grid grid-cols-4 h-11">
            <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
            <TabsTrigger value="pending_review" data-testid="filter-pending">Pending</TabsTrigger>
            <TabsTrigger value="finalized" data-testid="filter-finalized">Finalized</TabsTrigger>
            <TabsTrigger value="received" data-testid="filter-received">Received</TabsTrigger>
          </TabsList>
        </Tabs>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-kale-600" />
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-12">
            <History className="w-12 h-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500">No orders found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredOrders.map((order, index) => (
              <Link
                key={order.id}
                to={`/orders/${order.id}`}
                className={`block animate-in stagger-${(index % 4) + 1}`}
                data-testid={`history-order-${order.id}`}
              >
                <Card className="card-hover">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          {getStatusBadge(order.status)}
                          <span className="text-xs text-stone-400 font-mono">
                            #{order.order_name || order.id.substring(0, 8)}
                          </span>
                        </div>
                        <p className="text-sm text-stone-600">
                          <span className="font-medium font-mono">{order.items.length}</span> items •{" "}
                          <span className="font-medium font-mono">{getUniqueVendorCount(order.items)}</span> vendors
                        </p>
                        <p className="text-xs text-stone-400 mt-1">
                          {formatDate(order.created_at)} • {order.created_by_name}
                        </p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-stone-400 flex-shrink-0 mt-2" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
